/// <reference path="../types.d.ts" />

import history from '../history';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { api } from '../core/net';
import { showFeedback } from './feedback.actions';
import { Group } from './action-types';
import { Dispatch, Action } from 'redux';
import { HttpMethods } from '@codametrix/ui-common';

const chooseGroup = createAction<CMxAPI.Group>(Group.CHOOSE_GROUP);
const listEligibleGroupMembers = createAction<CMxAPI.GroupUser[]>(
  Group.LIST_GROUP_MEMBERS
);

const listItems = createAsyncThunk<
  CMxAPI.PageableList<CMxAPI.Group>,
  AppProps.listParams,
  { rejectValue: CMxCommonApp.SubmitError }
>(Group.LIST, async params => {
  const pageableList = await api<CMxAPI.PageableList<CMxAPI.Group>>({
    endpoint: `/users/groups/tenant/v1?&tenantId=${params.id}`,
    init: {
      method: HttpMethods.GET
    }
  });

  return pageableList;
});

const dispatchGetGroupMemberInfo = async (
  dispatch: Dispatch<Action>,
  group: CMxAPI.Group
) => {
  const groupId = group.id;

  const eligibleMembers = await api<CMxAPI.GroupUser[]>({
    endpoint: `/users/groups/${groupId}/users/v1?eligibleMembers=true`,
    init: {
      method: HttpMethods.GET
    }
  });

  dispatch(listEligibleGroupMembers(eligibleMembers) as any);
};

const getGroupDetail = createAsyncThunk<
  CMxAPI.Group,
  AppProps.actionParams<CMxAPI.Group>,
  { rejectValue: CMxCommonApp.SubmitError }
>(Group.FETCH_DETAIL, async (params, { dispatch }) => {
  const { data: group, path } = params;
  const id = group.id;

  if (id === undefined || id === null) {
    return {} as CMxAPI.Group;
  }

  const detail = await api<CMxAPI.Group>({
    endpoint: `/users/groups/${id}/users/v1`
  });

  dispatch(chooseGroup(group));
  history.push(path);
  dispatchGetGroupMemberInfo(dispatch as any, group);

  return detail;
});

const addGroupMember = createAsyncThunk<
  CMxAPI.GroupUser,
  AppProps.addGroupMemberParams,
  { rejectValue: CMxCommonApp.SubmitError }
>(Group.ADD_GROUP_USER, async (params, _thunkAPI) => {
  const { groupId, groupMembers } = params;

  const saved = await api<CMxAPI.GroupUser>({
    endpoint: `/users/groups/${groupId}/users/v1`,
    init: {
      method: HttpMethods.POST
    },
    body: groupMembers
  });

  return saved;
});

const saveGroup = createAsyncThunk<
  void,
  CMxAPI.Group,
  { rejectValue: CMxCommonApp.SubmitError }
>(Group.SAVE, async (group, { dispatch }) => {
  group = {
    ...group,
    active: true
  };

  // Updating group not supported on backend yet
  const isUpdate = group.id != null;
  let method: string = isUpdate ? HttpMethods.PUT : HttpMethods.POST;
  let endpoint: string = isUpdate
    ? `/users/groups/${group.id}/v1`
    : `/users/groups/v1`;

  await api<CMxAPI.Group>({
    endpoint,
    init: {
      method
    },
    body: group
  });

  dispatch(
    showFeedback({
      id: Date.now(),
      message: `Group Saved.`,
      dismissable: true,
      className: 'feedback-working'
    }) as any
  );
  history.goBack();
});

const deleteGroup = createAsyncThunk<
  void,
  number,
  { rejectValue: CMxCommonApp.SubmitError }
>(Group.DELETE, async (groupId, { dispatch }) => {
  const group = {
    id: groupId
  };

  let method: string = HttpMethods.DELETE;

  await api<CMxAPI.Group>({
    endpoint: `/users/groups/${groupId}/v1`,
    init: {
      method
    },
    body: group
  });

  dispatch(
    showFeedback({
      id: Date.now(),
      message: `Group Deleted.`,
      dismissable: true,
      className: 'feedback-working'
    }) as any
  );
  history.goBack();
});

export const groupActions = {
  chooseGroup,
  listItems,
  getGroupDetail,
  saveGroup,
  deleteGroup,
  listEligibleGroupMembers,
  addGroupMember
};
